"use client";
import { authClient } from "@repo/auth/client";
import { sessionQueryKey, useSessionQuery } from "@saas/auth/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState, useRef } from "react";
import { installAuthInterceptor } from "../../../../lib/auth-interceptor";
import { useAwsCredentials, broadcastAuthContextChange } from "../hooks/use-aws-credentials";
import { SessionContext } from "../lib/session-context";

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data: session } = useSessionQuery();
  const [loaded, setLoaded] = useState(!!session);
  const contextKey =
    session === undefined
      ? undefined
      : `${session?.user.id ?? ""}:${session?.session.activeOrganizationId ?? ""}`;
  const originalContext = useRef(contextKey);
  if (originalContext.current === undefined && contextKey !== undefined)
    originalContext.current = contextKey;
  const contextChanged = contextKey !== undefined && originalContext.current !== contextKey;
  useEffect(() => {
    if (!contextChanged) return;
    broadcastAuthContextChange();
    void queryClient.cancelQueries();
    queryClient.clear();
    window.location.replace("/app");
  }, [contextChanged, queryClient]);

  // Initialize AWS credentials hook with user ID and active organization ID
  const awsCredentials = useAwsCredentials(
    session?.user?.id,
    session?.session?.activeOrganizationId
  );

  useEffect(() => {
    // Install the global authentication interceptor
    installAuthInterceptor();
  }, []);

  useEffect(() => {
    if (session && !loaded) {
      setLoaded(true);
    }
  }, [session]);

  return (
    <SessionContext.Provider
      value={{
        loaded,
        session: session?.session ?? null,
        user: session?.user ?? null,
        aws: awsCredentials,
        reloadSession: async () => {
          const { data: newSession, error } = await authClient.getSession({
            query: {
              disableCookieCache: true,
            },
          });

          if (error) {
            throw new Error(error.message || "Failed to fetch session");
          }

          queryClient.setQueryData(sessionQueryKey, () => newSession);
        },
      }}
    >
      {contextChanged ? <p role="status">Refreshing your workspace…</p> : children}
    </SessionContext.Provider>
  );
}
